import { v } from 'h3-valibot';
import {
  floatingContactButtonConfigurationSchema,
  type ParsedContactOptionsSegment,
  type ParsedFloatingContactButton,
  type ParsedFloatingContactConfiguration,
  type ParsedFloatingContactSegment,
  type buttonConfigSchema,
  type segmentConfigSchema
} from '~/utils/floatingContactButton/configurationSchema';

export const FLOATING_CONTACT_BUTTON_DOCS_URL =
  'https://dev.azure.com/baslerweb/Basler%20Digital%20Experience%20Platform/_git/basler-webapp?path=/docs/contentful/floating-contact-button-configuration.md&version=GBdevelop&_a=preview';

type SegmentOutput = v.InferOutput<typeof segmentConfigSchema>;
type ButtonOutput = v.InferOutput<typeof buttonConfigSchema>;

export const stripDocumentationKeys = (parsed: unknown): unknown => {
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return parsed;
  }

  return Object.fromEntries(
    Object.entries(parsed as Record<string, unknown>).filter(
      ([key]) => !key.startsWith('_')
    )
  );
};

export const parseRawConfigurationInput = (raw: unknown): unknown => {
  if (typeof raw === 'string') {
    try {
      return stripDocumentationKeys(JSON.parse(raw) as unknown);
    } catch {
      return raw;
    }
  }

  return stripDocumentationKeys(raw);
};

const normalizeSegment = (
  segment: SegmentOutput
): ParsedFloatingContactSegment => {
  if (segment.id === 'contact-form') {
    return {
      id: 'contact-form',
      title: segment.title,
      iconName: segment.iconName,
      pardotUrl: segment.pardotUrl.href
    };
  }

  const optionsSegment: ParsedContactOptionsSegment = {
    id: 'contact-options',
    title: segment.title,
    iconName: segment.iconName
  };

  return optionsSegment;
};

const normalizeButton = (
  button: ButtonOutput
): ParsedFloatingContactButton => ({
  id: button.id,
  label: button.label,
  iconName: button.iconName,
  size: button.size,
  styleType: button.styleType,
  href: button.href,
  target: button.target,
  rel: button.rel,
  position: button.position,
  usePdpActionBarOffset: button.usePdpActionBarOffset,
  offset: button.offset,
  panel: button.panel
    ? {
        segments: button.panel.segments?.map((segment) =>
          normalizeSegment(segment)
        )
      }
    : undefined
});

export const parseFloatingContactConfiguration = (
  raw: unknown
):
  | { success: true; data: ParsedFloatingContactConfiguration }
  | { success: false; issues: v.BaseIssue<unknown>[] } => {
  const parsed = parseRawConfigurationInput(raw);
  const result = v.safeParse(floatingContactButtonConfigurationSchema, parsed);

  if (!result.success) {
    return { success: false, issues: result.issues };
  }

  return {
    success: true,
    data: {
      floatingButton: result.output.floatingButton ?? {},
      buttons: result.output.buttons.map((button) => normalizeButton(button))
    }
  };
};

export const findPanelButton = (
  configuration: ParsedFloatingContactConfiguration
): ParsedFloatingContactButton | undefined =>
  configuration.buttons.find((button) => Boolean(button.panel));

export const findSegment = <T extends ParsedFloatingContactSegment['id']>(
  button: ParsedFloatingContactButton | undefined,
  segmentId: T
): Extract<ParsedFloatingContactSegment, { id: T }> | undefined => {
  const segment = button?.panel?.segments?.find(
    (entry) => entry.id === segmentId
  );

  return segment as
    Extract<ParsedFloatingContactSegment, { id: T }> | undefined;
};

export const resolveFloatingContactPardotUrl = (
  raw: unknown
): string | undefined => {
  const parsed = parseFloatingContactConfiguration(raw);

  if (!parsed.success) {
    return undefined;
  }

  const panelButton = findPanelButton(parsed.data);
  const contactForm = findSegment(panelButton, 'contact-form');

  return contactForm?.pardotUrl;
};

export const hasValidFloatingContactSetup = (
  configuration: ParsedFloatingContactConfiguration
): boolean => {
  const panelButton = findPanelButton(configuration);

  if (!panelButton || configuration.buttons.length === 0) {
    return false;
  }

  const contactForm = findSegment(panelButton, 'contact-form');

  if (!contactForm) {
    return true;
  }

  return Boolean(contactForm.pardotUrl);
};
